import { defineCopy } from '@/copy';

/**
 * tournaments strings. Voice: short, funny, second person, rib the player and never the person.
 * No em dashes. Placeholders use {name} and go through `fill()`.
 */
export const copy = defineCopy('tournaments', {
  tile: 'Crown somebody tonight.',

  formats: {
    round_robin: 'Round robin',
    single_elim: 'Knockout',
  },
  formatBlurb: {
    round_robin: 'Everybody plays everybody. The table decides.',
    single_elim: 'Lose and you are out. Top seeds skip a round.',
  },
  modes: {
    '1v1': '1v1',
    '2v2': '2v2',
    '3v3': '3v3',
    '4v4': '4v4',
    '5v5': '5v5',
  },

  list: {
    title: 'Tournaments',
    line: 'No tournaments. Make one, crown someone.',
    empty: 'No tournaments yet',
    emptyBody: 'Draw a bracket. Somebody will take it way too seriously.',
    newButton: 'New tournament',
    liveNow: 'On now',
    past: 'Past tournaments',
    progress: '{done} of {total} games',
    champion: 'Champion: {name}',
    teams: '{n} teams',
    players: '{n} players',
    loading: 'Loading tournaments',
    failed: 'Could not load the tournaments. Check your signal.',
    retry: 'Try again',
    cancelledTag: 'Cancelled',
    liveTag: 'Live',
    doneTag: 'Done',
  },

  card: {
    title: 'Bracket night',
    next: '{a} vs {b} is up',
    playing: '{a} vs {b} is on court',
    waiting: 'Waiting on the next round',
    open: 'Open',
  },

  new: {
    title: 'New tournament',
    line: 'Draw it up. Argue about the seeds later.',
    format: 'Format',
    mode: 'Each game is',
    who: {
      title: 'Who is playing',
      hint: 'Tap names. Pick at least two teams worth of players.',
      count: '{n} picked, {teams} teams of {size}',
      countSolo: '{n} picked',
      needMore: 'Need {n} more for two teams',
      leftover: '{names} will not be on a team. Tap one more to even it up.',
      fromLink: 'Teams came from the idea card. Change the picks and they get dealt again.',
    },
    teams: {
      title: 'Teams',
      balance: 'Balance',
      shuffle: 'Shuffle',
      seed: 'Seed {n}',
      power: '{n} OVR',
      unrated: 'Rookies count as average.',
      keepTeams: 'Keep these teams',
      redeal: 'Deal them again',
    },
    rules: {
      title: 'House rules',
      preset: { quick11: 'To 11', to21: 'To 21', iso3: 'To 3' },
      line: '{scoring}, win by {winBy}',
      onesTwos: '1s and 2s',
      twosThrees: '2s and 3s',
    },
    double: {
      label: 'Play everybody twice',
      help: 'Home and away. Doubles the games, so budget the evening.',
    },
    name: {
      label: 'Name it',
      hint: 'The group chat will see this.',
      placeholder: 'Sunday Showdown',
    },
    run: {
      label: 'Part of {title}',
      help: 'Its games land in the run recap.',
    },
    preview: {
      games: '{n} games over {rounds} rounds',
      oneGame: '1 game',
      byes: '{n} byes for the top seeds',
      oneBye: '1 bye for the top seed',
    },
    names: [
      'Sunday Showdown',
      'The Gauntlet',
      'Bracket Night',
      'Chaos Cup',
      'Winner Takes The Pizza',
      'Ankle Breaker Open',
      'The Last Run',
      'Backboard Classic',
    ],
    draw: { round_robin: 'Draw the schedule', single_elim: 'Draw the bracket' },
    drawing: 'Drawing',
    failed: 'The tournament did not save. Try again.',
    offline:
      'No signal. A new tournament needs the server once, then it works on the court. Try again when you have a bar.',
    noCrew: 'Nobody here yet. Add the crew in Crew first.',
  },

  detail: {
    loading: 'Loading the bracket',
    missing: 'Tournament not found',
    missingBody: 'It got knocked out, or it never existed.',
    back: 'All tournaments',
    cancelledBanner: 'This one was cancelled. The games stay on the books.',
    tabs: { bracket: 'Bracket', table: 'Table', games: 'Games' },
    nextUp: 'Up next',
    playingNow: 'On court now',
    start: 'Start game',
    watch: 'Open the game',
    enterScore: 'Enter score',
    playedByHand: 'Played off the app',
    waiting: 'Waiting on the earlier rounds',
    bye: 'Bye',
    tbd: 'To be decided',
    winner: 'Winner',
    round: 'Round {n}',
    final: 'Final',
    semis: 'Semifinals',
    quarters: 'Quarterfinals',
    rest: 'Rests: {name}',
    status: {
      live: 'Playing',
      ready: 'Up next',
      pending: 'Waiting',
      done: 'Done',
      bye: 'Bye',
    },
    statusTag: {
      live: 'Live',
      final: 'Done',
      cancelled: 'Cancelled',
    },
    table: {
      team: 'Team',
      w: 'W',
      l: 'L',
      d: 'D',
      diff: '+/-',
      leaderNote: 'Level on wins? Head to head, then point difference, then points scored.',
      empty: 'Play a game and the table fills itself.',
    },
    admin: {
      title: 'Run the tournament',
      finish: 'Call it now',
      finishTitle: 'End it here?',
      finishBody: 'The team on top is crowned. The games not played stay unplayed.',
      finishConfirm: 'Hold to crown',
      cancel: 'Cancel tournament',
      cancelTitle: 'Cancel the whole thing?',
      cancelBody: 'The games already played stay on the books. The bracket is shelved.',
      cancelConfirm: 'Hold to cancel',
      remove: 'Delete it',
      removeTitle: 'Delete this tournament?',
      removeBody: 'It disappears from every list. The games stay.',
      removeConfirm: 'Hold to delete',
      failed: 'That did not go through. Try again.',
    },
    score: {
      title: 'Enter the score',
      line: 'For games played without the scoreboard.',
      save: 'Save score',
      saving: 'Saving',
      knockoutTie: 'A knockout game needs a winner.',
      failed: 'The score did not save. Try again.',
    },
    champion: {
      title: 'Champions',
      record: '{w}-{l}',
      recordDiff: '{w}-{l}, {diff} points',
      runnerUp: 'Runner-up: {name}',
      games: '{n} games played',
      noChampion: 'Called off without a champion. Everybody goes home even.',
      crowned: '{name} took it',
    },
    toastCreated: 'Bracket drawn. Go play.',
  },
});
